import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowRight,
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  User,
  CalendarCheck2,
  CheckCircle2,
  Clock,
  Loader2,
  MessageSquare,
} from 'lucide-react';
import Navbar from '../components/Navbar';
import FooterSection from '../components/FooterSection';
import SEO from '../components/SEO';
import { LanguageProvider } from '../context/LanguageContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELD_BASE =
  'w-full bg-black/40 border border-white/[0.10] focus:border-gold-500/60 rounded-xl px-4 py-3 text-[15px] text-white placeholder:text-white/35 outline-none transition-all';

// ---------------------------------------------------------------------------
// Schedule a Demo — lightweight booking form. Posts to a generic
// `/api/leads/contact` endpoint when available; otherwise gracefully falls
// back to opening a pre-filled mailto so the lead is never lost.
// ---------------------------------------------------------------------------

function Field({ label, required, hint, error, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-bold uppercase tracking-[0.18em] text-white/55 mb-2">
        {label} {required && <span className="text-gold-400">*</span>}
      </span>
      {children}
      {error && <p className="mt-1.5 text-xs text-red-400" role="alert">{error}</p>}
      {!error && hint && <p className="mt-1.5 text-xs text-white/40">{hint}</p>}
    </label>
  );
}

export default function PartnersScheduleDemo() {
  return (
    <LanguageProvider>
      <Inner />
    </LanguageProvider>
  );
}

function Inner() {
  const { t } = useTranslation();
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [form, setForm] = useState({
    organization_name: '',
    contact_name: '',
    email: '',
    phone: '',
    preferred_times: '',
    notes: '',
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (!form.organization_name) e.organization_name = t('partners.schedule.err_required');
    if (!form.contact_name) e.contact_name = t('partners.schedule.err_required');
    if (!form.email || !/^\S+@\S+\.\S+$/.test(form.email)) e.email = t('partners.schedule.err_email');
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error(t('partners.schedule.toast_check_fields'));
      return;
    }
    setSubmitting(true);
    try {
      try {
        await axios.post(`${API}/leads/contact`, {
          source: 'partners-schedule-demo',
          ...form,
        }, { timeout: 6000 });
      } catch {
        const subject = encodeURIComponent('NoFee Partners — Schedule a Demo');
        const body = encodeURIComponent(
          `Organization: ${form.organization_name}\n` +
          `Contact: ${form.contact_name}\n` +
          `Email: ${form.email}\n` +
          `Phone: ${form.phone}\n` +
          `Preferred times: ${form.preferred_times}\n\n` +
          `Notes:\n${form.notes}`
        );
        window.location.href = `mailto:partners@nofee.restaurant?subject=${subject}&body=${body}`;
      }
      setSent(true);
      toast.success(t('partners.schedule.toast_received'));
    } catch (err) {
      toast.error(t('partners.schedule.toast_error'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-black text-white antialiased">
      <SEO title="Schedule a Demo — NoFee Partners" description="Book a live demo with the NoFee Partnership Team." path="/partners/schedule-demo" noindex />
      <Navbar forceSolid />

      <main className="pt-32 lg:pt-40 pb-24">
        <div className="max-w-[920px] mx-auto px-6 lg:px-8">
          <Link
            to="/partners"
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-white/55 hover:text-gold-300 mb-6 transition-colors"
            data-testid="schedule-demo-back"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> {t('partners.schedule.back')}
          </Link>

          <p className="text-[11px] uppercase tracking-[0.28em] text-gold-400 font-bold mb-3">
            {t('partners.schedule.kicker')}
          </p>
          <h1
            className="text-4xl lg:text-6xl font-black tracking-[-0.02em] text-white leading-[1.04] mb-3"
            data-testid="schedule-demo-title"
          >
            {t('partners.schedule.title_a')} <span className="bg-gradient-to-b from-amber-200 to-gold-400 bg-clip-text text-transparent">{t('partners.schedule.title_b')}</span>.
          </h1>
          <p className="text-white/60 max-w-[640px] mb-10 leading-relaxed">
            {t('partners.schedule.subtitle')}
          </p>

          {!sent && (
            <form
              onSubmit={onSubmit}
              className="rounded-3xl border border-white/[0.08] bg-gradient-to-b from-zinc-900/50 to-black/30 p-8 lg:p-10"
              data-testid="schedule-demo-form"
            >
              <div className="grid lg:grid-cols-2 gap-5">
                <Field label={t('partners.schedule.field_org')} required error={errors.organization_name}>
                  <div className="relative">
                    <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.organization_name}
                      onChange={(e) => update('organization_name', e.target.value)}
                      placeholder="Boston Restaurant Association"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="schedule-demo-field-organization"
                    />
                  </div>
                </Field>

                <Field label={t('partners.schedule.field_contact')} required error={errors.contact_name}>
                  <div className="relative">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.contact_name}
                      onChange={(e) => update('contact_name', e.target.value)}
                      placeholder="Jane Doe"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="schedule-demo-field-contact"
                    />
                  </div>
                </Field>

                <Field label={t('partners.schedule.field_email')} required error={errors.email}>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => update('email', e.target.value)}
                      placeholder="you@organization.org"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="schedule-demo-field-email"
                    />
                  </div>
                </Field>

                <Field label={t('partners.schedule.field_phone')}>
                  <div className="relative">
                    <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.phone}
                      onChange={(e) => update('phone', e.target.value)}
                      placeholder="(617) 555-0123"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="schedule-demo-field-phone"
                    />
                  </div>
                </Field>
              </div>

              <div className="mt-5 grid gap-5">
                <Field label={t('partners.schedule.field_times')} hint={t('partners.schedule.field_times_hint')}>
                  <div className="relative">
                    <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/35" />
                    <input
                      value={form.preferred_times}
                      onChange={(e) => update('preferred_times', e.target.value)}
                      placeholder="Tuesdays or Thursdays 2–5pm ET"
                      className={`${FIELD_BASE} pl-11`}
                      data-testid="schedule-demo-field-times"
                    />
                  </div>
                </Field>

                <Field label={t('partners.schedule.field_notes')} hint={t('partners.schedule.field_notes_hint')}>
                  <div className="relative">
                    <MessageSquare className="absolute left-4 top-3.5 w-4 h-4 text-white/35" />
                    <textarea
                      value={form.notes}
                      onChange={(e) => update('notes', e.target.value)}
                      rows={3}
                      placeholder="We're a 120-member pizza association looking to roll this out by Q1…"
                      className={`${FIELD_BASE} pl-11 resize-none`}
                      data-testid="schedule-demo-field-notes"
                    />
                  </div>
                </Field>
              </div>

              <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                <p className="text-xs text-white/45">{t('partners.schedule.footnote')}</p>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 hover:from-amber-200 hover:to-gold-400 disabled:opacity-60 text-black font-extrabold text-[15px] transition-all"
                  data-testid="schedule-demo-submit"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarCheck2 className="w-4 h-4" />}
                  {t('partners.schedule.submit')} <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </form>
          )}

          {sent && (
            <div
              className="rounded-3xl border border-gold-500/30 bg-gradient-to-br from-gold-500/[0.06] to-black/30 p-8 lg:p-12 text-center"
              data-testid="schedule-demo-success"
            >
              <div className="w-16 h-16 rounded-2xl bg-gold-500 text-black flex items-center justify-center mx-auto mb-5">
                <CheckCircle2 className="w-8 h-8" />
              </div>
              <h2 className="text-2xl lg:text-3xl font-black tracking-tight text-white mb-2">
                {t('partners.schedule.received_title')}
              </h2>
              <p className="text-white/65 max-w-[480px] mx-auto mb-8 leading-relaxed">
                {t('partners.schedule.received_body_a')} <strong className="text-white">{form.contact_name}</strong>. {t('partners.schedule.received_body_b')} <span className="text-gold-300">{form.email}</span> {t('partners.schedule.received_body_c')}
              </p>
              <Link
                to="/partners"
                className="inline-flex items-center gap-2 h-12 px-6 rounded-2xl bg-gradient-to-b from-amber-300 to-gold-500 text-black font-extrabold text-[15px] transition-all"
                data-testid="schedule-demo-back-home"
              >
                {t('partners.schedule.received_back')} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          )}
        </div>
      </main>

      <FooterSection />
    </div>
  );
}
